import client from './client';
import { idbGet, idbSet, idbDelete } from '../utils/idbCache';

const MEMBER_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export const listMembers = (params) =>
  client.get('/fpa-members', { params }).then((r) => r.data);

export const getMember = (id) =>
  client.get(`/fpa-members/${id}`).then((r) => r.data);

// Cache-first member fetch used by the parcels map for hazard colouring.
// 24h TTL, but writes bust the entry (see invalidateMemberCache) — without
// that, editing a member's hazards left the parcel map showing the old
// colours for up to a day, which reads as "the hazard change didn't save".
// Other callers should use getMember().
export const getMemberCached = async (id) => {
  const key = `member:${id}`;
  const hit = await idbGet('members', key);
  if (hit) return hit;
  const fresh = await getMember(id);
  await idbSet('members', key, fresh, MEMBER_CACHE_TTL_MS);
  return fresh;
};

// ── Tenant-wide bulk cache (server-driven version) ─────────────────── //

// `{ tenant_id, version, member_count }`. `version` is the ISO timestamp of
// the newest member updated_at; bumps whenever any member is created/edited.
export const getMembersCacheStatus = (tenantId) =>
  client
    .get(`/fpa-members/by-tenant/${tenantId}/cache-status`)
    .then((r) => r.data);

// Full tenant member list in one round-trip. Returns `{ tenant_id, version,
// members, total }`. The version is the same shape as cache-status so the
// frontend can store it alongside the blob and skip the refetch when it
// hasn't changed.
export const listMembersBulk = (tenantId) =>
  client.get(`/fpa-members/by-tenant/${tenantId}/bulk`).then((r) => r.data);

// Version-keyed bulk cache. Always probes cache-status (cheap) then returns
// the IndexedDB blob if its stored version matches; otherwise refetches and
// updates the cache. No TTL — version mismatch is the only invalidation.
export const getMembersBulkCached = async (tenantId) => {
  const status = await getMembersCacheStatus(tenantId);
  const cacheKey = `tenant:${tenantId}:bulk`;
  const hit = await idbGet('members', cacheKey);
  if (hit && hit.version === status.version) return hit.data;
  const fresh = await listMembersBulk(tenantId);
  await idbSet('members', cacheKey, { version: fresh.version, data: fresh });
  return fresh;
};

// Full tenant member list including `current_year_paid`, for the members
// list page. Separate from listMembersBulk's lean (map) payload, and cached
// under its own key so the two never collide.
export const listMembersForList = (tenantId) =>
  client
    .get(`/fpa-members/by-tenant/${tenantId}/bulk`, { params: { include_paid: true } })
    .then((r) => r.data);

// Version-keyed cache for the members list page. Probes the cheap
// cache-status endpoint, returns the IndexedDB blob when its stored version
// matches, otherwise refetches (with paid status) and updates the cache.
export const getMembersForListCached = async (tenantId) => {
  const status = await getMembersCacheStatus(tenantId);
  const cacheKey = `tenant:${tenantId}:list`;
  const hit = await idbGet('members', cacheKey);
  if (hit && hit.version === status.version) return hit.data;
  const fresh = await listMembersForList(tenantId);
  await idbSet('members', cacheKey, { version: fresh.version, data: fresh });
  return fresh;
};

export const createMember = (data) =>
  client.post('/fpa-members', data).then((r) => r.data);

// Drop a member's cached copy after a write. The tenant-wide `bulk`/`list`
// blobs are deliberately NOT busted here: they're version-keyed against
// getMembersCacheStatus, which the backend bumps on any member write, so
// they self-heal on the next probe.
export const invalidateMemberCache = (id) =>
  idbDelete('members', `member:${id}`).catch(() => {});

export const updateMember = async (id, data) => {
  const r = await client.put(`/fpa-members/${id}`, data);
  await invalidateMemberCache(id);
  return r.data;
};

export const deleteMember = async (id) => {
  const r = await client.delete(`/fpa-members/${id}`);
  await invalidateMemberCache(id);
  return r.data;
};

export const bulkDeleteMembers = async (memberIds) => {
  const r = await client.post('/fpa-members/bulk-delete', { member_ids: memberIds });
  await Promise.all((memberIds || []).map((id) => invalidateMemberCache(id)));
  return r.data;
};

// Per-contact routes. Prefer these over PUT /fpa-members/{id} for adding or
// editing a single contact: that endpoint reconciles the WHOLE contacts
// array and deletes anything omitted, so a one-contact payload would wipe
// the rest. The routes have existed for a while but nothing called them.
export const createMemberContact = async (memberId, data) => {
  const r = await client.post(`/fpa-members/${memberId}/contacts`, data);
  await invalidateMemberCache(memberId);
  return r.data;
};

export const updateMemberContact = async (memberId, contactId, data) => {
  const r = await client.put(`/fpa-members/${memberId}/contacts/${contactId}`, data);
  await invalidateMemberCache(memberId);
  return r.data;
};

export const deleteMemberContact = async (memberId, contactId) => {
  const r = await client.delete(`/fpa-members/${memberId}/contacts/${contactId}`);
  await invalidateMemberCache(memberId);
  return r.data;
};
export const getMemberPayments = (id) =>
  client.get(`/fpa-members/${id}/payments`).then((r) => r.data);

export const createPayment = (memberId, data) =>
  client.post(`/fpa-members/${memberId}/payments`, data).then((r) => r.data);

export const updatePayment = (memberId, paymentId, data) =>
  client.put(`/fpa-members/${memberId}/payments/${paymentId}`, data).then((r) => r.data);

export const deletePayment = (memberId, paymentId) =>
  client.delete(`/fpa-members/${memberId}/payments/${paymentId}`).then((r) => r.data);

export const exportMembers = (params) =>
  client.get('/fpa-members/export', { params, responseType: 'blob' });

export const downloadTemplate = (params) =>
  client.get('/fpa-members/template', { params, responseType: 'blob' });

export const importMembers = (formData, config) =>
  client.post('/fpa-members/import', formData, config).then((r) => r.data);
